import { supabase } from '@/utils/supabase';
import { createComponentLogger } from './logger';
import { isSessionlessPublicRoute, isAuthAdjacentRoute } from './routeUtils';

const log = createComponentLogger('SessionRecovery', 'auth');

export class SessionRecovery {
  /**
   * Attempt to recover an expired or invalid session.
   * Never runs on sessionless public routes or auth-adjacent routes
   * (`/`, `/auth/redirect`, `/auth/callback*`) — those must not be
   * bounced mid-flow by a failed refresh.
   */
  static async recoverSession(): Promise<boolean> {
    try {
      const currentPath = window.location.pathname;

      if (isSessionlessPublicRoute(currentPath) || isAuthAdjacentRoute(currentPath)) {
        log.debug('Skipping recovery on non-protected route', { path: currentPath });
        return false;
      }

      log.info('Attempting session recovery');

      const { data, error } = await supabase.auth.refreshSession();

      if (error || !data.session) {
        log.warn('Session recovery failed, forcing re-authentication');
        await supabase.auth.signOut();

        // Defense-in-depth: earlier guard already covers these, but leave the
        // explicit check so a future refactor can't accidentally force-redirect
        // from a sessionless or auth-adjacent route.
        if (!isSessionlessPublicRoute(currentPath) && !isAuthAdjacentRoute(currentPath)) {
          window.location.href = '/';
        }
        return false;
      }

      log.info('Session recovered successfully');
      localStorage.setItem('supabase_session', JSON.stringify(data.session));
      return true;
    } catch (error) {
      log.error('Session recovery failed', { error: error instanceof Error ? error.message : String(error) });
      return false;
    }
  }

  /**
   * Ensure we have a valid session, attempting recovery if needed
   */
  static async ensureValidSession(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        log.debug('No session found, attempting recovery');
        return await this.recoverSession();
      }

      const expiresAt = session.expires_at;
      if (expiresAt) {
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = expiresAt - now;

        if (timeUntilExpiry < 300) {
          log.info('Session expiring soon, refreshing');
          return await this.recoverSession();
        }
      }

      return true;
    } catch (error) {
      log.error('Session validation failed', { error: error instanceof Error ? error.message : String(error) });
      return false;
    }
  }

  /**
   * Get a valid session with retry logic.
   *
   * Route behavior:
   *   - Sessionless public routes: skip entirely, return null.
   *   - Auth-adjacent routes (entry + OAuth callback): still return an
   *     existing session from getSession() so AuthContext can restore the
   *     user on `/auth/redirect`; but skip the getUser+retry and
   *     recoverSession fallback because those can bounce the flow.
   *   - Fully protected routes: full fallback chain.
   */
  static async getValidSession(): Promise<any> {
    try {
      const currentPath = window.location.pathname;

      if (isSessionlessPublicRoute(currentPath)) {
        log.debug('On sessionless public route, skipping session lookup', { path: currentPath });
        return null;
      }

      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        return session;
      }

      // No existing session. On auth-adjacent routes we stop here — no
      // refresh/recovery allowed because it can redirect the browser away.
      if (isAuthAdjacentRoute(currentPath)) {
        log.debug('No session on auth-adjacent route; skipping recovery fallback', { path: currentPath });
        return null;
      }

      // Protected routes: try getUser() then a single recovery attempt.
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: { session: retrySession } } = await supabase.auth.getSession();
        if (retrySession) {
          return retrySession;
        }
      }

      log.debug('No session found, attempting single recovery');
      const recovered = await this.recoverSession();
      if (recovered) {
        const { data: { session: recoveredSession } } = await supabase.auth.getSession();
        if (recoveredSession) {
          return recoveredSession;
        }
      }

    } catch (error) {
      log.error('Session recovery failed in getValidSession', { error: error instanceof Error ? error.message : String(error) });
    }

    log.debug('No valid session found');
    return null;
  }

  /**
   * Clear all authentication data
   */
  static async clearAuthData(): Promise<void> {
    try {
      localStorage.removeItem('supabase_session');
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.clear();

      localStorage.removeItem('gmail_connection_cache');

      await supabase.auth.signOut();
    } catch (error) {
      log.error('Error clearing auth data', { error: error instanceof Error ? error.message : String(error) });
    }
  }
}
